import React, { useCallback, useEffect, useState } from 'react';
import { Send, Copy, Check, FileText, Undo2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { mensajeErrorApi } from '../apiErrors';

function formatFecha(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-EC', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function Campo({ etiqueta, valor, nota }) {
  const [copiado, setCopiado] = useState(false);
  const texto = valor === null || valor === undefined ? '' : String(valor);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      toast.error('No se pudo copiar automáticamente. Selecciona el texto y copia a mano.');
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '150px minmax(0, 1fr) auto', gap: 'var(--spacing-2)', alignItems: 'center', padding: '4px 0' }}>
      <span className="text-xs text-secondary">{etiqueta}</span>
      <span className="text-sm" style={{ overflowWrap: 'anywhere' }}>
        {texto || <span className="text-secondary">—</span>}
        {nota && <span className="text-xs text-secondary"> {nota}</span>}
      </span>
      <button type="button" className="btn-ghost" onClick={copiar} disabled={!texto} aria-label={`Copiar ${etiqueta}`} title="Copiar">
        {copiado ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}

/**
 * Prealertas que faltan por registrar en el sistema del operador logístico. Muestra cada una con
 * los datos en el mismo orden de su formulario, para copiarlos sin transcribir. El botón «Enviar al
 * Operador» abre ese formulario (la dirección sale del parámetro `operador_url_prealerta`); al
 * terminar se marca como registrada y sale de la lista.
 */
export default function PrealertasOperadorPanel({ onCambio }) {
  const [vista, setVista] = useState('pendientes');
  const [paquetes, setPaquetes] = useState([]);
  const [urlFormulario, setUrlFormulario] = useState('');
  const [cargando, setCargando] = useState(true);
  const [abiertas, setAbiertas] = useState({});

  const cargar = useCallback(() => {
    setCargando(true);
    api.get('/paquetes/pendientes-operador/', { params: vista === 'enviadas' ? { enviadas: 1 } : {} })
      .then((res) => {
        setPaquetes(res.data.paquetes || []);
        setUrlFormulario(res.data.url_formulario || '');
      })
      .catch((err) => toast.error(mensajeErrorApi(err, 'No se pudieron cargar las prealertas.')))
      .finally(() => setCargando(false));
  }, [vista]);

  useEffect(() => { cargar(); }, [cargar]);

  const enviarAlOperador = (p) => {
    if (!urlFormulario) {
      toast.error('Falta la dirección del formulario del operador: configúrala en Parámetros (operador_url_prealerta).');
      return;
    }
    window.open(urlFormulario, '_blank', 'noopener');
    setAbiertas((a) => ({ ...a, [p.id]: true }));
  };

  const marcar = async (p, marcado) => {
    try {
      await api.post(`/paquetes/${p.id}/marcar-prealertado-operador/`, { marcado });
      setPaquetes((lista) => lista.filter((x) => x.id !== p.id));
      toast.success(marcado ? 'Marcada como registrada en el operador.' : 'Devuelta a pendientes.');
      if (onCambio) onCambio();
    } catch (err) {
      toast.error(mensajeErrorApi(err, 'No se pudo actualizar la prealerta.'));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
        <h2 style={{ margin: 0 }}>Prealertas al Operador</h2>
        <button type="button" className="btn-secondary" onClick={cargar}><RefreshCw size={14} /> Actualizar</button>
      </div>
      <p className="text-sm text-secondary">
        Cada prealerta que crea un cliente queda aquí hasta que la registres en el sistema del operador. Copia los datos en el
        orden de su formulario y, al guardar, márcala como registrada.
      </p>

      <div className="tabs-nav" style={{ marginBottom: 'var(--spacing-3)' }}>
        <button type="button" className={`tab-btn ${vista === 'pendientes' ? 'active' : ''}`} onClick={() => setVista('pendientes')}>Pendientes</button>
        <button type="button" className={`tab-btn ${vista === 'enviadas' ? 'active' : ''}`} onClick={() => setVista('enviadas')}>Últimas registradas</button>
      </div>

      {cargando ? (
        <p className="text-secondary">Cargando...</p>
      ) : paquetes.length === 0 ? (
        <div className="empty-state">
          <p>{vista === 'pendientes' ? 'No hay prealertas pendientes. Todo está registrado en el operador.' : 'Todavía no has registrado ninguna.'}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 'var(--spacing-4)' }}>
          {paquetes.map((p) => (
            <div key={p.id} className="card" style={{ padding: 'var(--spacing-4)', minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                <strong>{p.cliente_nombre}</strong>
                <span className="text-xs text-secondary">
                  {vista === 'enviadas' ? `Registrada ${formatFecha(p.prealertado_operador_en)}` : `Prealertada ${formatFecha(p.fecha_prealerta)}`}
                </span>
              </div>

              <Campo etiqueta="Tracking" valor={p.tracking} />
              <Campo etiqueta="Tienda" valor={p.tienda} />
              <Campo etiqueta="Contenido" valor={p.descripcion} />
              <Campo etiqueta="Destinatario" valor={p.cliente_nombre} nota={`(casillero ${p.codigo_casillero})`} />
              <Campo etiqueta="Valor comercial" valor={p.valor_declarado} />
              <Campo etiqueta="Valor asegurado" valor={p.valor_declarado} nota="(igual al valor comercial)" />
              <Campo etiqueta="Orden de compra" valor={p.orden_o_factura} />
              <Campo etiqueta="Notas" valor={p.instrucciones_especiales} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', padding: '4px 0' }}>
                <span className="text-xs text-secondary" style={{ width: '150px' }}>Copia de factura</span>
                {p.factura_url ? (
                  <a href={p.factura_url} target="_blank" rel="noopener noreferrer" download className="text-sm"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <FileText size={14} /> Descargar factura
                  </a>
                ) : (
                  <span className="text-sm text-secondary">Sin factura adjunta</span>
                )}
              </div>

              <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', marginTop: 'var(--spacing-3)' }}>
                {vista === 'pendientes' ? (
                  <>
                    <button type="button" className={abiertas[p.id] ? 'btn-secondary' : 'btn-primary'} onClick={() => enviarAlOperador(p)}>
                      <Send size={14} /> Enviar al Operador
                    </button>
                    <button type="button" className={abiertas[p.id] ? 'btn-primary' : 'btn-secondary'} onClick={() => marcar(p, true)}>
                      <Check size={14} /> Ya la registré
                    </button>
                  </>
                ) : (
                  <button type="button" className="btn-secondary" onClick={() => marcar(p, false)}>
                    <Undo2 size={14} /> Devolver a pendientes
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
